import COURSE_TYPES from './types';
import {
	type ICourse,
	type IInstitution,
	type IMetadata,
	type ISection,
} from 'src/models';
import ANSWER_TYPES from '../Answers/types';

export interface State {
	courses: ICourse[];
	institutionCourses: any[];
	usersCourses: any[];
	course: ICourse;
	exam: ICourse;
	isSaved: boolean;
	examReview: ICourse;
	isLoading: boolean;
	isLoadingOne: boolean;
	isLoadingExam: boolean;
	isLoadingReview: boolean;
	isDownloadingReport: boolean;
	isDownloadingBulk: boolean;
	page: number;
	pageSize: number;
	metadata: IMetadata;
	examTracker: {
		startedAt: any;
	};
	reports: {
		all: {
			isLoading: boolean;
			data: any[];
		};
		institution: {
			isLoading: boolean;
			data: any[];
		};
		qualifications: {
			data: any[];
		};
	};
	isLoadingCertificate: boolean;
	userCertificate: any;
	review: {
		institution: IInstitution;
		course: ICourse;
	};
}

const initialState: State = {
	courses: [],
	institutionCourses: [],
	usersCourses: [],
	course: {},
	exam: {},
	isSaved: false,
	examReview: {},
	isLoading: false,
	isLoadingOne: false,
	isLoadingExam: false,
	isLoadingReview: false,
	isDownloadingReport: false,
	isDownloadingBulk: false,
	page: 1,
	pageSize: 20,
	examTracker: {
		startedAt: '',
	},
	metadata: {
		totalPages: 0,
		totalResults: 0,
	},
	reports: {
		all: {
			isLoading: false,
			data: [],
		},
		institution: {
			isLoading: false,
			data: [],
		},
		qualifications: {
			data: [],
		},
	},
	isLoadingCertificate: false,
	userCertificate: {},
	review: {
		institution: {},
		course: {},
	},
};

const reducer = (state: State = initialState, action: any): State => {
	const { type, payload } = action;
	switch (type) {
		case COURSE_TYPES.GET_ALL: {
			return {
				...state,
				isLoading: true,
				page: payload.page || state.page,
				pageSize: payload.pageSize || state.pageSize,
			};
		}

		case COURSE_TYPES.SET: {
			return {
				...state,
				[payload.type]: payload.value,
			};
		}

		case COURSE_TYPES.SET_ALL: {
			return {
				...state,
				isLoading: false,
				courses: payload.data,
				metadata: payload.metadata || state.metadata,
			};
		}

		case COURSE_TYPES.GET_ONE: {
			return {
				...state,
				isLoadingOne: true,
			};
		}

		case COURSE_TYPES.SET_ONE: {
			return {
				...state,
				isLoadingOne: false,
				course: payload.course,
			};
		}

		case ANSWER_TYPES.UPDATE_USER_SECTION_ANSWERS:
		case COURSE_TYPES.GET_EXAM_REVIEW:
		case COURSE_TYPES.GET_EXAM: {
			return {
				...state,
				isLoadingExam: true,
			};
		}

		case COURSE_TYPES.SET_EXAM: {
			const exam: ICourse = payload.exam;
			const _sections: ISection[] = [];
			exam.sections?.forEach((section: ISection, index: number) => {
				if (
					(section.questionsInExam || 0) > 0 &&
					(section.questions?.length || 0) > 0
				)
					_sections.push(section);
			});
			exam.sections = _sections;

			return {
				...state,
				isLoadingExam: false,
				exam,
			};
		}

		case COURSE_TYPES.SET_EXAM_REVIEW: {
			return {
				...state,
				examReview: payload.data,
				isLoadingExam: false,
			};
		}

		case COURSE_TYPES.SET_ANSWERED_SECTION: {
			const exam = { ...state.exam };
			const index = exam.sections?.findIndex(
				(section: ISection) => section.id === payload.section,
			);
			// @ts-ignore
			exam.sections[index] = payload.section;

			return {
				...state,
				exam,
				isSaved: Boolean(payload.isSaved),
			};
		}

		case COURSE_TYPES.SET_INSTITUTION_COURSES: {
			return {
				...state,
				institutionCourses: payload.institutionCourses,
			};
		}

		case COURSE_TYPES.INITIAL_STATE: {
			return state.isSaved
				? {
						...initialState,
						exam: state.exam,
				  }
				: initialState;
		}

		case COURSE_TYPES.GET_REPORT: {
			return {
				...state,
				reports: {
					...state.reports,
					[payload.type]: {
						// @ts-ignore
						...state.reports[payload.type],
						isLoading: true,
					},
				},
			};
		}

		case COURSE_TYPES.SET_REPORT: {
			return {
				...state,
				reports: {
					...state.reports,
					[payload.type]: {
						// @ts-ignore
						...state.reports[payload.type],
						isLoading: false,
						data: payload.data,
					},
				},
			};
		}

		case COURSE_TYPES.DOWNLOAD_REPORT: {
			return {
				...state,
				isDownloadingReport: true,
			};
		}

		case COURSE_TYPES.GET_USER_CERTIFICATE: {
			return {
				...state,
				isLoadingCertificate: true,
			};
		}

		case COURSE_TYPES.SET_USER_CERTIFICATE: {
			return {
				...state,
				isLoadingCertificate: false,
				userCertificate: payload.data,
			};
		}

		case COURSE_TYPES.SET_REVIEW_INSTITUTION: {
			return {
				...state,
				review: {
					...state.review,
					institution: payload.institution,
				},
			};
		}

		case COURSE_TYPES.SET_REVIEW_COURSE: {
			return {
				...state,
				review: {
					...state.review,
					course: payload.course,
				},
			};
		}

		case COURSE_TYPES.DOWNLOAD_USER_EXAM:
		case COURSE_TYPES.DOWNLOAD_USERS_CERTIFICATE:
		case COURSE_TYPES.DOWNLOAD_ALL_USERS_EXAM: {
			return {
				...state,
				isDownloadingBulk: true,
			};
		}

		default:
			return state;
	}
};

export default reducer;
